import React, { memo, useCallback, useRef } from "react";
import cx from "classnames";
import { useDashboardEditorContext } from "../../../../hook/context/editor";
import type { DashboardModelT } from "../../../../schema/dashboard";
import { Actions } from "./Actions";
import { Status } from "./Status";
import { SimpleInlineTextEditor } from "../Editor/InlineEditor/Text";
import t from "../../../../../../../i18n";
import {
  Header,
  headerActions,
  HeaderDescription,
  headerStatus,
  headerTitle,
  headerWithScrollAnimation,
} from "./_styles/Header";
import { useDashboardEditorState } from "../../../../hook/store/editor";
import { useScrollAnimation } from "./hook/useScrollAnimation";
import { useProject } from "../../../../../../../api/useProjects";
import { useTranslations } from "../../../../../../../api/useTranslations";
import { useDashboard } from "../../../../hook/api/dashboard";

export const DashboardReadWriteHeader = memo(
  ({
    className,
    projectId,
    dashboardId,
  }: {
    className?: string;
    projectId: string;
    dashboardId: string;
  }) => {
    const translations = useTranslations();
    const { dashboard } = useDashboard(projectId, dashboardId);
    const editor = useDashboardEditorContext();
    const editorState = useDashboardEditorState(editor?.store);
    const displayedDashboard = editorState?.dashboard ?? dashboard;

    const updateTitle = useCallback(
      (value: string) =>
        editor?.updateDashboard({
          ...editorState?.dashboard,
          title: value || "",
        } as unknown as DashboardModelT),
      [editorState?.dashboard, editor],
    );

    const updateDescription = useCallback(
      (value: string) =>
        void editor?.updateDashboard({
          ...editorState?.dashboard,
          description: value,
        } as unknown as DashboardModelT),
      [editorState?.dashboard, editor],
    );

    const root = useRef<HTMLDivElement>(null);
    const project = useProject(projectId);

    useScrollAnimation(root);

    if (!displayedDashboard || !project) return null;

    return (
      <Header ref={root} className={cx(className, headerWithScrollAnimation)}>
        <Status
          className={headerStatus}
          projectId={projectId}
          dashboardId={dashboardId}
          published={displayedDashboard.published}
          readonly={displayedDashboard.readonly}
          username={displayedDashboard.createdByUserName}
        />
        <div className={headerActions}>
          <Actions dashboardId={dashboardId} projectId={projectId} />
        </div>
        <>
          <SimpleInlineTextEditor
            key={`title-${displayedDashboard.updatedAt}`}
            className={cx("h2", headerTitle)}
            dashboardId={dashboardId}
            ignoreLikeBreaks
            initialValue={displayedDashboard.title ?? t("project.dashboard.title.empty")}
            onUpdate={updateTitle}
            placehoder={t("project.dashboard.title.placeholder", translations)}
            initialFocus
          />
          <SimpleInlineTextEditor
            key={`description-${displayedDashboard.updatedAt}`}
            className={cx("body1", HeaderDescription)}
            dashboardId={dashboardId}
            initialValue={editorState?.dashboard?.description ?? ""}
            onUpdate={updateDescription}
            placehoder={t("project.dashboard.description.placeholder", translations)}
          />
        </>
      </Header>
    );
  },
);
